import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  ILmsRecommendationDecision,
  TLmsContentPreview,
  TLmsRecommendedContent,
  TLmsSuggestion,
  TLmsSuggestionList,
} from "@/types/lms";

/** Recommended LMS content on an intake request (plan §6.7, use case #18, NG-676).
 *
 *  **Neither call reaches the vendor.** The read scores the request's title against the
 *  catalog cache the connector's pull fills, and the write records one person's decision.
 *  So both sit inside the sub-1s read / sub-2.5s write budgets, and the panel needs no
 *  progress indicator — a spinner here would be pure theatre.
 */
export class LmsRecommendationService extends ApiService {
  private base(slug: string, submissionId: string): string {
    return `/api/workspaces/${slug}/intake/submissions/${submissionId}/lms-recommendations/`;
  }

  async listSuggestions(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<TLmsSuggestionList> {
    return this.get(this.base(slug, submissionId), config);
  }

  /** What Cognota already stores about one suggestion, for the Preview dialog (NG-1858).
   *  Reads the catalog cache, the landed payload and the project links; no vendor call. */
  async getPreview(
    slug: string,
    submissionId: string,
    target: Pick<TLmsSuggestion, "connection_id" | "kind" | "external_id">,
    config?: ApiRequestConfig,
  ): Promise<TLmsContentPreview> {
    return this.get(`${this.base(slug, submissionId)}preview/`, {
      ...config,
      params: {
        connection_id: target.connection_id,
        kind: target.kind,
        external_id: target.external_id,
      },
    });
  }

  async decide(
    slug: string,
    submissionId: string,
    payload: ILmsRecommendationDecision,
  ): Promise<TLmsRecommendedContent> {
    return this.post(this.base(slug, submissionId), payload);
  }
}
